import React, { useEffect, useState } from 'react';
import { api } from '../App';
import ExportBar from '../components/ExportBar';

// Title-case helper: turns "BOTTOM WEAR" + "Black" → "Bottom Wear" + "Black"
// so the UI renders one consistent style regardless of how ERP stored values.
// Leaves part-codes (alphanumeric blobs, no spaces, has digits) alone.
function titleCase(s) {
  if (!s) return s;
  const str = String(s);
  if (!/\s/.test(str) && /[0-9]/.test(str) && str.length > 6) return str;
  return str.toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase());
}

function dayLabel(d) {
  if (d == null) return '';
  if (d < -7) return `${Math.abs(d)}d overdue`;
  if (d < 0) return `${Math.abs(d)}d late`;
  if (d === 0) return 'Due today';
  if (d <= 7) return `${d}d left`;
  return `${d}d`;
}

const C = (key, label, type = 'text', total = false) => ({ key, label, type, total });
const ORDER_COLS = [C('tas_no', 'Order No'), C('order_type', 'Type'), C('buyer', 'Buyer'), C('style_name', 'Style'), C('factory', 'Factory'), C('merchandiser', 'Merchandiser'), C('orderqty', 'Order Qty', 'int', true), C('bal_qty', 'Balance Qty', 'int', true), C('order_value', 'Value', 'money', true), C('completion_pct', 'Done %', 'pct'), C('Delay_days', 'Delay Days', 'int'), C('delivery_dt', 'Delivery', 'date'), C('health', 'Health')];

export default function Orders({ user, onOrderClick, filter: incomingFilter, clearFilter, onBackToBuyers }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('critical');
  const [bucket, setBucket] = useState(null); // null | 'pending' | 'completed' — when buyer filter is on, pick a bucket first
  const _curFY = (() => {
    const n = new Date();
    const sy = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1;
    return String(sy).slice(2) + String(sy + 1).slice(2);
  })();
  const [yearFilter, setYearFilter] = useState(_curFY); // default = current FY
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');
  const [searchQ, setSearchQ] = useState('');

  useEffect(() => {
    setLoading(true);
    setBucket(incomingFilter ? 'pending' : null);
    setFilter('critical');
    setYearFilter(_curFY);
    const qs = incomingFilter
      ? `?type=${encodeURIComponent(incomingFilter.type)}&buyer=${encodeURIComponent(incomingFilter.buyer)}`
      : '';
    api('/orders' + qs).then(d => { setOrders(Array.isArray(d) ? d : []); setLoading(false); });
  }, [incomingFilter]);

  // Indian financial year (Apr 1 – Mar 31). fyKey '2627' → 1-Apr-2026 to 31-Mar-2027
  function inFY(dateVal, fyKey) {
    if (!dateVal) return false;
    const d = new Date(dateVal);
    const startY = 2000 + parseInt(fyKey.slice(0, 2));
    const start = new Date(startY, 3, 1);                 // Apr 1
    const end   = new Date(startY + 1, 2, 31, 23, 59, 59);// Mar 31 next year
    return d >= start && d <= end;
  }

  // Year filter on enquiry_date (falls back to delivery_dt if missing)
  function inCustom(dateVal) {
    if (!dateVal) return false;
    const d = new Date(dateVal);
    if (customFrom && d < new Date(customFrom)) return false;
    if (customTo) { const e = new Date(customTo); e.setHours(23,59,59); if (d > e) return false; }
    return true;
  }
  const yearScopedRaw = yearFilter === 'all' ? orders
                     : yearFilter === 'custom'
                       ? (customFrom || customTo ? orders.filter(o => inCustom(o.enquiry_date || o.delivery_dt)) : orders)
                       : orders.filter(o => inFY(o.enquiry_date || o.delivery_dt, yearFilter));

  // Free-text search across tas_no, factory, merchandiser, buyer, style_name
  const q = searchQ.trim().toLowerCase();
  const yearScoped = !q ? yearScopedRaw : yearScopedRaw.filter(o => {
    const haystack = `${o.tas_no || ''} ${o.factory || ''} ${o.merchandiser || ''} ${o.buyer || ''} ${o.style_name || ''} ${o.entryno || ''}`.toLowerCase();
    return haystack.includes(q);
  });

  // Pending vs Completed split
  const isCompleted = o => (o.bal_qty || 0) <= 0 || o.completion_pct === 100;
  const pendingOrders   = yearScoped.filter(o => !isCompleted(o));
  const completedOrders = yearScoped.filter(o =>  isCompleted(o));

  // Bucket-scoped list (only matters when buyer filter is on)
  const bucketScoped = !incomingFilter ? yearScoped
                     : bucket === 'pending'   ? pendingOrders
                     : bucket === 'completed' ? completedOrders
                     : yearScoped;

  // Bucket rule:
  //   Critical = delivery date passed AND stage also behind (current_stage_delay > 0)
  //   Delayed  = delivery date passed BUT stage still on track
  const stageBehind = o => (o.current_stage_delay != null) && (o.current_stage_delay > 0);
  const isCritical = o => o.Delay_days != null && o.Delay_days < 0 &&  stageBehind(o);
  const isDelayed  = o => o.Delay_days != null && o.Delay_days < 0 && !stageBehind(o);

  const tabs = [
    { key: 'critical', label: `Critical (${bucketScoped.filter(isCritical).length})` },
    { key: 'delayed',  label: `Delayed (${bucketScoped.filter(isDelayed).length})` },
  ];

  const filtered = filter === 'critical' ? bucketScoped.filter(isCritical)
    : filter === 'delayed'   ? bucketScoped.filter(isDelayed)
    : bucketScoped.filter(isCritical);  // default = Critical

  if (loading) return <div className="loading">LOADING ORDERS...</div>;

  // ===== INTERMEDIATE: Pending / Completed cards (only when buyer filter is set & no bucket chosen yet) =====
  if (incomingFilter && !bucket) {
    const totPendingPcs = pendingOrders.reduce((a, o) => a + (o.bal_qty || 0), 0);
    const totCompletedAmt = completedOrders.reduce((a, o) => a + (o.order_value || 0), 0);
    const totPendingAmt = pendingOrders.reduce((a, o) => a + (o.order_value || 0), 0);

    return (
      <div className="screen">
        <div className="header">
          <div>
            <div className="header-logo">RISHAB</div>
            <div className="header-sub">{incomingFilter.type === 'garment' ? 'TAS' : 'GSP'} · {incomingFilter.buyer}</div>
          </div>
          <button onClick={onBackToBuyers || clearFilter} style={{ background: 'none', border: '1px solid var(--border2)', color: 'var(--text3)', fontSize: 10, padding: '4px 10px', borderRadius: 3, cursor: 'pointer', letterSpacing: 1 }}>← BACK</button>
        </div>

        <div style={{ padding: '32px 20px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 11, letterSpacing: 2 }}>
          {orders.length} TOTAL {incomingFilter.type === 'garment' ? 'TAS' : 'GSP'} ORDERS
        </div>

        <div style={{ marginTop: 20, padding: '0 16px', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <div
            onClick={() => setBucket('pending')}
            style={{
              padding: '20px 32px', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer',
              textAlign: 'center', minWidth: 180, transition: 'all 0.2s',
              background: 'var(--red-bg)'
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.background = 'var(--red-bg)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--red-bg)'; }}
          >
            <div style={{ color: 'var(--red)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 36 }}>{pendingOrders.length}</div>
            <div style={{ color: 'var(--red)', fontSize: 12, letterSpacing: 3, marginTop: 4 }}>PENDING</div>
            <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 6 }}>{totPendingPcs.toLocaleString()} pcs</div>
            <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 2 }}>₹{(totPendingAmt/100000).toFixed(1)}L value</div>
            <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 10, letterSpacing: 1 }}>VIEW LIST →</div>
          </div>

          <div
            onClick={() => setBucket('completed')}
            style={{
              padding: '20px 32px', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer',
              textAlign: 'center', minWidth: 180, transition: 'all 0.2s',
              background: 'var(--green-bg)'
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.background = 'var(--green-bg)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--green-bg)'; }}
          >
            <div style={{ color: 'var(--green)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 36 }}>{completedOrders.length}</div>
            <div style={{ color: 'var(--green)', fontSize: 12, letterSpacing: 3, marginTop: 4 }}>COMPLETED</div>
            <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 6 }}>delivered</div>
            <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 2 }}>₹{(totCompletedAmt/100000).toFixed(1)}L value</div>
            <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 10, letterSpacing: 1 }}>VIEW LIST →</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="header">
        <div>
          <div className="header-logo">RISHAB</div>
          <div className="header-sub">{user.isManagement ? 'All Orders' : 'My Orders'}</div>
        </div>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 28, color: 'var(--gold)' }}>{bucketScoped.length}</div>
      </div>

      {incomingFilter && (
        <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {bucket && (
            <button onClick={() => onBackToBuyers ? onBackToBuyers() : clearFilter()} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 18, cursor: 'pointer', padding: 0 }}>←</button>
          )}
          <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1 }}>FILTER</div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: incomingFilter.type === 'garment' ? 'var(--gold-bg)' : 'rgba(183,155,201,0.10)',
            border: `1px solid ${incomingFilter.type === 'garment' ? 'var(--gold)' : 'var(--teal)'}`,
            color: incomingFilter.type === 'garment' ? 'var(--gold)' : 'var(--teal)',
            padding: '4px 10px', borderRadius: 12, fontSize: 12
          }}>
            <span>{incomingFilter.type === 'garment' ? 'TAS' : 'GSP'}</span>
            <span>·</span>
            <span>{incomingFilter.buyer}</span>
            <span onClick={clearFilter} style={{ cursor: 'pointer', marginLeft: 4, opacity: 0.7 }}>✕</span>
          </div>
          {bucket && (
            <div
              onClick={() => setBucket(bucket === 'pending' ? 'completed' : 'pending')}
              title="Click to toggle Pending / Completed"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                background: bucket === 'pending' ? 'var(--red-bg)' : 'var(--green-bg)',
                border: `1px solid ${bucket === 'pending' ? 'var(--red)' : 'var(--green)'}`,
                color: bucket === 'pending' ? 'var(--red)' : 'var(--green)',
                padding: '4px 10px', borderRadius: 12, fontSize: 12, letterSpacing: 1
              }}
            >
              <span>{bucket === 'pending' ? 'PENDING' : 'COMPLETED'}</span>
              <span style={{ opacity: 0.6, fontSize: 10 }}>⇄</span>
            </div>
          )}
        </div>
      )}

      <ExportBar reportKey={`orders-${bucket}`} title={`Orders — ${bucket === 'pending' ? 'Pending' : 'Completed'}`} subtitle={[filter ? `filter: ${filter}` : '', yearFilter && yearFilter !== 'all' ? `FY: ${yearFilter}` : '', searchQ ? `search: ${searchQ}` : ''].filter(Boolean).join(' | ')} sections={[{ name: 'Orders', columns: ORDER_COLS, rows: filtered }]} source={{ endpoint: '/orders', params: incomingFilter ? { type: incomingFilter.type, buyer: incomingFilter.buyer } : {},
        filters: [
          ...(searchQ ? [{ t: 'search', q: searchQ, keys: ['tas_no', 'buyer', 'factory', 'merchandiser', 'style_name'] }] : []),
          ...(bucket === 'completed' ? [{ t: 'gte', k: 'completion_pct', v: 100 }]
              : bucket === 'pending' ? [{ t: 'lt', k: 'completion_pct', v: 100 }] : []),
          ...(filter === 'delayed' ? [{ t: 'gt', k: 'Delay_days', v: 0 }] : []),
        ] }} />

      {/* Search box */}
      <div style={{ padding: '8px 16px 4px', position: 'relative' }}>
        <span style={{ position: 'absolute', left: 28, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 12 }}>🔍</span>
        <input
          type="text"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          placeholder="Search TAS, factory, merchandiser, style…"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '8px 32px 8px 32px',
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6,
            color: 'var(--text)', fontSize: 12, outline: 'none'
          }}
          onFocus={e => e.target.style.borderColor = 'var(--gold)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
        {searchQ && (
          <span
            onClick={() => setSearchQ('')}
            style={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', cursor: 'pointer', fontSize: 12 }}
          >✕</span>
        )}
      </div>

      {/* Accounting Year (FY Apr-Mar) filter row */}
      <div style={{ padding: '8px 16px 4px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1, marginRight: 4 }}>FY (BY ENQUIRY DATE)</span>
        {[['all','ALL DATA'], ['2627','FY 26-27'], ['2526','FY 25-26']].map(([y, label]) => (
          <button
            key={y}
            onClick={() => setYearFilter(y)}
            style={{
              background: yearFilter === y ? 'var(--gold-bg)' : 'transparent',
              border: `1px solid ${yearFilter === y ? 'var(--gold)' : 'var(--border)'}`,
              color: yearFilter === y ? 'var(--gold)' : 'var(--text3)',
              padding: '4px 12px', borderRadius: 12, fontSize: 11, cursor: 'pointer', letterSpacing: 1
            }}
          >
            {label}
          </button>
        ))}
        {yearFilter === 'custom' && (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 11, padding: '3px 6px', outline: 'none' }} />
            <span style={{ color: 'var(--text3)', fontSize: 11 }}>→</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 11, padding: '3px 6px', outline: 'none' }} />
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text3)' }}>
          {yearScoped.length} of {orders.length}
        </span>
      </div>

      <div className="filter-tabs">
        {tabs.map(t => (
          <button key={t.key} className={`filter-tab ${filter === t.key ? 'active' : ''}`} onClick={() => setFilter(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 30, color: 'var(--text3)', fontSize: 12 }}>
          No orders for this filter
        </div>
      )}

      {/* Grid of compact order boxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 12,
        padding: '12px 16px 24px'
      }}>
        {[...filtered].sort((a, b) => {
          const ka = (String(a.tas_no || '').match(/\d+/g) || []).map(Number);
          const kb = (String(b.tas_no || '').match(/\d+/g) || []).map(Number);
          for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
            const x = ka[i] ?? 0, y = kb[i] ?? 0;
            if (x !== y) return x - y;
          }
          return String(a.tas_no || '').localeCompare(String(b.tas_no || ''));
        }).map(o => {
          const isOverdue = o.Delay_days < 0;
          const accent = o.health === 'critical' ? 'var(--red)'
                       : o.health === 'overdue'  ? 'var(--red)'
                       : o.health === 'at_risk'  ? 'var(--yellow)'
                       : 'var(--green)';
          return (
            <div
              key={o.iid + '_' + o.tas_no}
              onClick={() => onOrderClick(o)}
              style={{
                background: 'var(--bg2)',
                border: 'none',
                borderLeft: `3px solid ${accent}`,
                borderRadius: 12,
                padding: 14,
                cursor: 'pointer',
                transition: 'background 0.15s, transform 0.15s',
                display: 'flex',
                flexDirection: 'column',
                gap: 8
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg2)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {/* Top row: TAS + delay badge */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {o.tas_no}
                    {o.order_type === 'fabric' && <span style={{ fontSize: 8, background: 'var(--teal-bg)', color: 'var(--teal)', padding: '1px 5px', borderRadius: 3, letterSpacing: 1, fontWeight: 600 }}>GSP</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCase(o.buyer)}</div>
                </div>
                <div style={{
                  fontSize: 9, padding: '3px 7px', borderRadius: 4, letterSpacing: 0.5, fontWeight: 600,
                  background: isOverdue ? 'var(--red-bg)' : 'var(--green-bg)',
                  color: isOverdue ? 'var(--red)' : 'var(--green)',
                  whiteSpace: 'nowrap', flexShrink: 0
                }}>
                  {dayLabel(o.Delay_days)}
                </div>
              </div>

              {/* Progress bar */}
              <div>
                <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: accent, width: `${o.completion_pct || 0}%`, borderRadius: 2 }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3, textAlign: 'right' }}>{o.completion_pct || 0}%</div>
              </div>

              {/* Bottom row: factory + qty + value */}
              <div style={{ fontSize: 10, color: 'var(--text3)', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100 }} title={o.factory}>🏭 {titleCase(o.factory) || '—'}</span>
                <span>📦 {(o.bal_qty || o.orderqty || 0).toLocaleString()}</span>
                {o.order_value > 0 && <span style={{ color: 'var(--gold)', marginLeft: 'auto' }}>₹{(o.order_value/100000).toFixed(1)}L</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
